<script lang="ts" setup>
import {ExtendedProject, Role} from "@/types";
import ProjectLayout from "@/Pages/projects/layouts/ProjectLayout.vue";
import BaseLayout from "@/layouts/BaseLayout.vue";
import SelectUserDialog from "@/Pages/projects/users/components/SelectUserDialog.vue";
import {ref} from "vue";
import {Head, router, useForm} from "@inertiajs/vue3";
import axios from "axios";
import InfoCard from "@/components/cards/InfoCard.vue";
import {route} from "ziggy-js";
import AccessesTable from "@/Pages/projects/users/components/AccessesTable.vue";
import NoMemberCard from "@/Pages/projects/users/components/NoMemberCard.vue";
import {watchThrottled} from "@vueuse/core";

interface Props {
    project: ExtendedProject,
    role: Role,
    accesses: Array<any>,
    membersCount: number,
    adminCount: number,
}

const props = defineProps<Props>()
defineOptions({
    layout: BaseLayout
})

const search = ref('')
const isDialogOpen = ref(false)
const isLoading = ref(false)
const form = useForm({
    user: null,
    role: 'member',
    dta: null
})
const users = ref([])

const submit = () => {
    form.post(route('projects.users.store', {project: props.project.slug}), {
        preserveScroll: true,
        onSuccess: () => {
            isDialogOpen.value = false
            form.reset()
            router.reload({
                only: ['accesses']
            })
        }
    })
}

const query = (search: string) => {
    if (!search) {
        users.value = []
        return
    }

    isLoading.value = true

    if (search.length < 1) {
        users.value = []
        isLoading.value = false
        return
    }

    axios.get(
        route('users.index'),
        {
            params: {
                search: search,
                project_slug: props.project.slug,
            }
        }
    ).then(({ data }) => {
        users.value = data.data
    }).catch(() => {
        users.value = []
    }).finally(() => {
        isLoading.value = false
    })
}

watchThrottled(search, (newValue) => {
    query(newValue)
}, {throttle: 150})
</script>

<template>
    <Head title="Manage access" />
    <project-layout
        :role="props.role"
        :project="props.project"
    >
        <select-user-dialog
            v-model="form"
            v-model:search-input="search"
            v-model:is-dialog-open="isDialogOpen"
            :is-loading="isLoading"
            :project="project.name"
            :items="users"
            @click:confirm="submit"
        />
        <h1 class="font-weight-medium text-h5 mt-5">
            Who has access
        </h1>
        <v-divider class="my-5" />
        <v-container>
            <v-card
                :flat="true"
                class="mb-5"
            >
                <v-row>
                    <v-col
                        cols="4"
                    >
                        <info-card
                            :title="`${props.project.is_public ? 'PUBLIC' : 'PRIVATE'} PROJECT`"
                            :icon="props.project.is_public ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                            :url="route('projects.edit', {project: props.project.slug})"
                        >
                            <p
                                v-if="!props.project.is_public"
                                class="black--text"
                            >
                                Only those with access to this project can view it.
                            </p>
                            <p
                                v-if="props.project.is_public"
                                class="black--text"
                            >
                                This project is public and visible to anyone.
                            </p>
                        </info-card>
                    </v-col>
                    <v-col
                        cols="4"
                    >
                        <info-card
                            title="COLLABORATORS"
                            icon="mdi-account-multiple-outline"
                        >
                            <p class="black--text">
                                <b>{{ membersCount }}</b> people have read access to this project.
                            </p>
                        </info-card>
                    </v-col>
                    <v-col
                        cols="4"
                    >
                        <info-card
                            title="ADMIN ACCESS"
                            icon="mdi-account-multiple-outline"
                        >
                            <p class="black--text">
                                <b>{{ adminCount }}</b> people have admin access to this project.
                            </p>
                        </info-card>
                    </v-col>
                </v-row>
                <v-card-text class="mt-10">
                    <v-row>
                        <span class="text-h5 black--text">Manage access</span>
                        <v-spacer />
                        <v-btn
                            v-if="props.project.has_groups"
                            variant="text"
                            size="small"
                            class="text-none text-body-2 mr-1"
                            color="primary"
                            @click="router.visit(route('projects.groups.create', {project: props.project.slug}))"
                        >
                            Create group
                        </v-btn>

                        <success-btn
                            size="small"
                            class="mr-1"
                            @click="isDialogOpen = true"
                        >
                            Add people
                        </success-btn>
                        <success-btn
                            v-if="props.project.has_groups"
                            size="small"
                            @click="router.visit(route('projects.groups.index', {project: props.project.slug}))"
                        >
                            Assign group
                        </success-btn>
                    </v-row>
                </v-card-text>
            </v-card>
            <no-member-card
                v-if="accesses.length === 0"
                @click="isDialogOpen = true"
            />
            <accesses-table
                v-if="accesses.length > 0"
                :project="props.project"
                :items="accesses"
                :loading="isLoading"
            />
        </v-container>
    </project-layout>
</template>

<style scoped>
</style>
